import React, { useEffect, useRef } from "react";
import * as d3 from "d3";
import { updatedScheme10 } from "../../util/chartColorScheme";

/**
 * New Customer Deals Won Stacked Bar Chart
 *
 * Displays a stacked bar chart showing # of deals or ACV by month by source
 */
function NewCustomerDealsWonStackedBar({ data, title, sources, yAxisLabel = "Value" }) {
  const chartRef = useRef(null);

  useEffect(() => {
    if (!chartRef.current) return;

    d3.select(chartRef.current).selectAll("*").remove();

    const margin = { top: 50, right: 30, bottom: 80, left: 70 };
    const containerWidth =
      chartRef.current.getBoundingClientRect().width || 500;
    const width = Math.max(300, containerWidth - margin.left - margin.right);
    const height = 350;

    // Handle empty data - show empty chart with title
    if (
      !data ||
      !Array.isArray(data) ||
      data.length === 0 ||
      !sources ||
      sources.length === 0
    ) {
      const svg = d3
        .select(chartRef.current)
        .append("svg")
        .attr("width", width + margin.left + margin.right)
        .attr("height", height + margin.top + margin.bottom)
        .style("display", "block")
        .append("g")
        .attr("transform", `translate(${margin.left},${margin.top})`);

      // Title
      svg
        .append("text")
        .attr("x", width / 2)
        .attr("y", -25)
        .attr("text-anchor", "middle")
        .attr("font-size", "14px")
        .attr("font-weight", "bold")
        .text(title || "");

      // No data message
      svg
        .append("text")
        .attr("x", width / 2)
        .attr("y", height / 2)
        .attr("text-anchor", "middle")
        .attr("font-size", "14px")
        .attr("fill", "#999")
        .text("No data available");

      return;
    }

    // Data exists - render full chart
    const svg = d3
      .select(chartRef.current)
      .append("svg")
      .attr("width", width + margin.left + margin.right)
      .attr("height", height + margin.top + margin.bottom)
      .style("display", "block")
      .append("g")
      .attr("transform", `translate(${margin.left},${margin.top})`);

    // X Scale
    const monthDomain = data.map((d) => d.yearMonth).filter(Boolean);
    const xScale = d3
      .scaleBand()
      .domain(monthDomain)
      .range([0, width])
      .padding(0.3);

    // Create stacked data
    const stack = d3.stack().keys(sources);
    const stackedData = stack(data);

    // Y Scale - find max of stacked values
    const maxValue = d3.max(stackedData, (layer) =>
      d3.max(layer, (d) => d[1])
    ) || 0;

    const yScale = d3
      .scaleLinear()
      .domain([0, maxValue * 1.1])
      .nice()
      .range([height, 0]);

    // Grid lines
    svg
      .append("g")
      .attr("class", "grid")
      .call(d3.axisLeft(yScale).ticks(6).tickSize(-width).tickFormat(""))
      .selectAll("line")
      .style("stroke", "#e0e0e0")
      .style("stroke-opacity", 0.7);

    svg.selectAll(".domain").remove();

    // X Axis
    svg
      .append("g")
      .attr("transform", `translate(0,${height})`)
      .call(
        d3
          .axisBottom(xScale)
          .tickSize(0)
          .tickFormat((d) => {
            const item = data.find((it) => it.yearMonth === d);
            return item ? item.month : d;
          }),
      )
      .selectAll("text")
      .style("font-size", "11px")
      .style("text-anchor", "middle");

    // Y Axis
    svg
      .append("g")
      .call(
        d3
          .axisLeft(yScale)
          .ticks(6)
          .tickFormat((d) => d),
      )
      .selectAll("text")
      .style("font-size", "10px");

    // Color scale using updatedScheme10
    const colorScale = d3.scaleOrdinal(updatedScheme10).domain(sources);

    // Draw stacked bars
    stackedData.forEach((layer) => {
      svg
        .selectAll(`.bar-${layer.key}`)
        .data(layer)
        .enter()
        .append("rect")
        .attr("class", `bar-${layer.key}`)
        .attr("x", (d) => xScale(d.data.yearMonth))
        .attr("y", (d) => yScale(d[1]))
        .attr("width", xScale.bandwidth())
        .attr("height", (d) => Math.max(yScale(d[0]) - yScale(d[1]), 0))
        .attr("fill", colorScale(layer.key))
        .attr("stroke", "#fff")
        .attr("stroke-width", 0.5);
    });

    // Add value labels inside bars (white text) if space is available
    stackedData.forEach((layer) => {
      svg
        .selectAll(`.bar-label-${layer.key}`)
        .data(layer)
        .enter()
        .append("text")
        .attr("class", `bar-label-${layer.key}`)
        .attr("x", (d) => xScale(d.data.yearMonth) + xScale.bandwidth() / 2)
        .attr("y", (d) => yScale(d[1]) + (yScale(d[0]) - yScale(d[1])) / 2)
        .attr("text-anchor", "middle")
        .attr("dominant-baseline", "middle")
        .attr("font-size", "10px")
        .attr("font-weight", "bold")
        .attr("fill", "white")
        .text((d) => {
          const barHeight = yScale(d[0]) - yScale(d[1]);
          const value = d[1] - d[0];
          // Only show label if bar height is at least 18px and value is not 0
          if (barHeight >= 18 && value !== 0) {
            // Format: show decimal for ACV values, integer for counts
            return typeof value === 'number' && value % 1 !== 0
              ? value.toFixed(1)
              : Math.round(value);
          }
          return "";
        });
    });

    // Total labels on top of each bar
    svg
      .selectAll(".total-label")
      .data(data)
      .enter()
      .append("text")
      .attr("class", "total-label")
      .attr("x", (d) => xScale(d.yearMonth) + xScale.bandwidth() / 2)
      .attr("y", (d) => yScale(d.Total) - 5)
      .attr("text-anchor", "middle")
      .attr("font-size", "10px")
      .attr("font-weight", "bold")
      .attr("fill", "#333")
      .text((d) => d.Total > 0 ? (typeof d.Total === 'number' && d.Total % 1 !== 0 ? d.Total.toFixed(1) : d.Total) : "");

    // Title
    svg
      .append("text")
      .attr("x", width / 2)
      .attr("y", -25)
      .attr("text-anchor", "middle")
      .attr("font-size", "14px")
      .attr("font-weight", "bold")
      .text(title || "");

    // Y axis label
    svg
      .append("text")
      .attr("transform", "rotate(-90)")
      .attr("x", -height / 2)
      .attr("y", -50)
      .attr("text-anchor", "middle")
      .attr("font-size", "11px")
      .text(yAxisLabel);

    // Legend
    const legendContainer = svg
      .append("g")
      .attr("transform", `translate(0, ${height + 35})`);

    const legendItemWidth = width / sources.length;

    sources.forEach((source, index) => {
      const legendItem = legendContainer
        .append("g")
        .attr("transform", `translate(${index * legendItemWidth + legendItemWidth / 2 - 30}, 0)`);

      legendItem
        .append("rect")
        .attr("width", 12)
        .attr("height", 12)
        .attr("fill", colorScale(source));

      legendItem
        .append("text")
        .attr("x", 16)
        .attr("y", 10)
        .attr("font-size", "10px")
        .text(source);
    });
  }, [data, title, sources, yAxisLabel]);

  return (
    <div
      ref={chartRef}
      id="printChart"
      style={{
        width: "100%",
        minHeight: "480px",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    />
  );
}

export default NewCustomerDealsWonStackedBar;
